import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

/** Mirrors the API-side import limits so problems surface before the request. */
export const SECRET_KEY_PATTERN = /^[A-Z][A-Z0-9_]*$/;
export const MAX_IMPORT_KEYS = 200;
export const MAX_IMPORT_VALUE_LENGTH = 32_768;
/** Upper bound for an uploaded file, comfortably above 200 maximum-size keys. */
export const MAX_IMPORT_FILE_BYTES = 1_048_576;

export type SecretImportConflictStrategy = "overwrite" | "skip";

export interface SecretsImportSubmission {
  secrets: Record<string, string>;
  conflictStrategy: SecretImportConflictStrategy;
}

export interface SecretsImportOutcome {
  created: string[];
  updated: string[];
  skipped: string[];
}

export type SecretsParseResult =
  | { secrets: Record<string, string>; error: null }
  | { secrets: null; error: string };

/**
 * Parses and validates a pasted or uploaded bulk-import payload. Error
 * messages name the offending key but never include any value.
 */
export function parseSecretsJson(text: string): SecretsParseResult {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return { secrets: null, error: "Paste or upload a JSON object to import." };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { secrets: null, error: "That is not valid JSON." };
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return {
      secrets: null,
      error:
        'Expected a flat JSON object, for example {"DATABASE_URL": "..."}.',
    };
  }

  const entries = Object.entries(parsed as Record<string, unknown>);
  if (entries.length === 0) {
    return { secrets: null, error: "The JSON object has no keys." };
  }
  if (entries.length > MAX_IMPORT_KEYS) {
    return {
      secrets: null,
      error: `Import at most ${MAX_IMPORT_KEYS} keys at a time (found ${entries.length}).`,
    };
  }

  const secrets: Record<string, string> = {};
  for (const [key, value] of entries) {
    if (!SECRET_KEY_PATTERN.test(key)) {
      return {
        secrets: null,
        error: `Key "${key}" must be UPPER_SNAKE_CASE, for example DATABASE_URL.`,
      };
    }
    if (typeof value !== "string") {
      return {
        secrets: null,
        error: `Value for "${key}" must be a string. Nested objects and arrays are not supported.`,
      };
    }
    if (value.length === 0) {
      return { secrets: null, error: `Value for "${key}" is empty.` };
    }
    if (value.length > MAX_IMPORT_VALUE_LENGTH) {
      return {
        secrets: null,
        error: `Value for "${key}" is longer than ${MAX_IMPORT_VALUE_LENGTH} characters.`,
      };
    }
    secrets[key] = value;
  }

  return { secrets, error: null };
}

/** Short, value-free summary of an import for a notification message. */
export function describeImportOutcome(outcome: SecretsImportOutcome): string {
  const parts: string[] = [];
  if (outcome.created.length > 0) parts.push(`${outcome.created.length} added`);
  if (outcome.updated.length > 0) {
    parts.push(`${outcome.updated.length} overwritten`);
  }
  if (outcome.skipped.length > 0) {
    parts.push(`${outcome.skipped.length} skipped`);
  }
  return parts.length > 0 ? parts.join(", ") : "no changes applied";
}

/** Reads an uploaded file as text. FileReader keeps jsdom and older
 * browsers supported, where `Blob.text()` is unavailable. */
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.onload = () =>
      resolve(typeof reader.result === "string" ? reader.result : "");
    reader.readAsText(file);
  });
}

interface SecretsImportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Human-readable scope used in dialog copy, e.g. "project". */
  scopeLabel: string;
  /** Keys that already exist at this scope — drives the conflict preview. */
  existingKeys: string[];
  onImport: (
    submission: SecretsImportSubmission,
  ) => Promise<SecretsImportOutcome>;
  /** Called after a successful import so the caller can refresh and notify. */
  onImported: (outcome: SecretsImportOutcome) => void;
}

/**
 * SecretsImportDialog — bulk-imports a flat `{"KEY":"value"}` JSON object into
 * one secret scope. Values are parsed in the browser, previewed as masked
 * rows, and never rendered or logged. Keys that already exist are listed so
 * the user can choose to overwrite them or keep the stored values.
 */
export function SecretsImportDialog({
  open,
  onClose,
  scopeLabel,
  existingKeys,
  onImport,
  onImported,
}: SecretsImportDialogProps): React.ReactElement {
  const [source, setSource] = useState<"paste" | "upload">("paste");
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [strategy, setStrategy] =
    useState<SecretImportConflictStrategy>("skip");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setSource("paste");
    setText("");
    setFileName(null);
    setFileError(null);
    setStrategy("skip");
    setSubmitError(null);
  }, [open]);

  const parsed = useMemo(() => parseSecretsJson(text), [text]);
  const existing = useMemo(() => new Set(existingKeys), [existingKeys]);

  const rows = useMemo(() => {
    if (!parsed.secrets) return [];
    return Object.keys(parsed.secrets)
      .sort()
      .map((key) => ({ key, conflict: existing.has(key) }));
  }, [parsed.secrets, existing]);

  const conflictCount = rows.filter((row) => row.conflict).length;
  const newCount = rows.length - conflictCount;
  const applyCount = strategy === "skip" ? newCount : rows.length;

  const handleFile = useCallback(async (file: File | undefined) => {
    if (!file) return;
    setFileError(null);
    setSubmitError(null);
    if (file.size > MAX_IMPORT_FILE_BYTES) {
      setFileName(file.name);
      setText("");
      setFileError(
        `${file.name} is larger than ${Math.round(MAX_IMPORT_FILE_BYTES / 1024)} KB.`,
      );
      return;
    }
    try {
      const contents = await readFileAsText(file);
      setFileName(file.name);
      setText(contents);
    } catch {
      setFileName(file.name);
      setText("");
      setFileError(`Could not read ${file.name}.`);
    }
  }, []);

  const handleSubmit = async () => {
    if (!parsed.secrets) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const outcome = await onImport({
        secrets: parsed.secrets,
        conflictStrategy: strategy,
      });
      onImported(outcome);
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "The import could not be applied.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const showParseError = text.trim().length > 0 && parsed.error !== null;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="secrets-import-title"
    >
      <DialogTitle id="secrets-import-title">
        Import {scopeLabel} secrets from JSON
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Provide a flat JSON object of UPPER_SNAKE_CASE keys and string
            values, for example{" "}
            <Box component="code" sx={{ fontFamily: "monospace" }}>
              {'{"DATABASE_URL": "postgres://..."}'}
            </Box>
            . Values are never displayed after import.
          </Typography>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={source}
            onChange={(_event, next: "paste" | "upload" | null) => {
              if (!next) return;
              setSource(next);
            }}
            aria-label="Import source"
          >
            <ToggleButton value="paste">Paste JSON</ToggleButton>
            <ToggleButton value="upload">Upload file</ToggleButton>
          </ToggleButtonGroup>

          {source === "paste" ? (
            <TextField
              autoFocus
              label="Secrets JSON"
              value={text}
              onChange={(event) => {
                setText(event.target.value);
                setFileName(null);
                setFileError(null);
                setSubmitError(null);
              }}
              fullWidth
              multiline
              minRows={6}
              maxRows={14}
              placeholder={
                '{\n  "DATABASE_URL": "postgres://...",\n  "API_KEY": "..."\n}'
              }
              inputProps={{
                spellCheck: false,
                style: { fontFamily: "monospace", fontSize: "0.8125rem" },
              }}
            />
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                Choose .json file
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                aria-label="Secrets JSON file"
                hidden
                onChange={(event) => {
                  const input = event.target;
                  void handleFile(input.files?.[0]).finally(() => {
                    // Allow re-selecting the same file after a failed parse.
                    input.value = "";
                  });
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {fileName ?? "No file selected"}
              </Typography>
            </Stack>
          )}

          {fileError && <Alert severity="error">{fileError}</Alert>}
          {showParseError && <Alert severity="error">{parsed.error}</Alert>}
          {submitError && <Alert severity="error">{submitError}</Alert>}

          {parsed.secrets && (
            <Stack spacing={1}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                aria-live="polite"
              >
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${newCount} new`}
                />
                <Chip
                  size="small"
                  color={conflictCount > 0 ? "warning" : "default"}
                  variant="outlined"
                  label={`${conflictCount} already exist`}
                />
              </Stack>

              {conflictCount > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Keys that already exist
                  </Typography>
                  <RadioGroup
                    value={strategy}
                    onChange={(event) =>
                      setStrategy(
                        event.target.value as SecretImportConflictStrategy,
                      )
                    }
                  >
                    <FormControlLabel
                      value="skip"
                      control={<Radio size="small" />}
                      label="Keep the stored values (skip those keys)"
                    />
                    <FormControlLabel
                      value="overwrite"
                      control={<Radio size="small" />}
                      label="Overwrite them with the imported values"
                    />
                  </RadioGroup>
                </Box>
              )}

              <TableContainer component={Paper} variant="outlined">
                <Table size="small" aria-label="Secrets to import">
                  <TableHead>
                    <TableRow>
                      <TableCell>Key</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            fontWeight={600}
                          >
                            {row.key}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            color="text.secondary"
                          >
                            ••••••••
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {!row.conflict ? (
                            <Chip size="small" variant="outlined" label="New" />
                          ) : strategy === "skip" ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label="Skipped"
                            />
                          ) : (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label="Overwritten"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={submitting || !parsed.secrets || applyCount === 0}
        >
          {submitting ? (
            <CircularProgress size={20} />
          ) : applyCount === 1 ? (
            "Import 1 secret"
          ) : (
            `Import ${applyCount} secrets`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SecretsImportDialog;
